import React, { useState } from 'react';

const Footer = () => {
  // State for the email input
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Handler for form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setStatus('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    setStatus('Sending...');

    try {
      // This will call your Express.js backend
      const response = await fetch('https://hi-way-89-backend.onrender.com/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email,
          timestamp: new Date().toISOString() 
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setStatus('✅ Thank you for subscribing! Check your email for confirmation.');
        setEmail(''); // Clear the input
      } else {
        setStatus(`❌ ${result.error || 'Failed to subscribe. Please try again.'}`);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setStatus('❌ Network error. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Social media icons component
  const SocialIcons = () => (
    <div className="flex space-x-4">
      {[
        {
          name: 'Instagram',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11a4 4 0 10-8 0 4 4 0 008 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2a10 10 0 00-7 17 10 10 0 0014 0 10 10 0 00-7-17z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.5 6.5h.01" />
            </svg>
          ),
          url: "https://www.instagram.com/hiway89ent?igsh=Zm42ZjRtYmoyN3l4"
        },
        {
          name: 'X (Twitter)',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
            </svg>
          ),
          url: "https://x.com/hiway89ent?s=21&t=W37y8xb6JUNjJS1xSpoj4Q"
        },
        {
          name: 'TikTok',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.589 6.686a4.793 4.793 0 01-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 01-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 113.183-4.51v-3.5a6.329 6.329 0 105.608 6.216 6.33 6.33 0 003.771-11.345z"/>
            </svg>
          ),
          url: "https://www.tiktok.com/@hiway89ent?_r=1&_t=ZS-92RJZi3mFE2"
        }
      ].map((social) => (
        <a
          key={social.name}
          href={social.url}
          className="text-gray-400 hover:text-white transition-all duration-300 transform hover:scale-110"
          aria-label={social.name}
          target="_blank"
          rel="noopener noreferrer"
        >
          {social.icon}
        </a>
      ))}
    </div>
  );

  return (
    <footer className="bg-black text-white border-t border-gray-800">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <img 
                src="https://hi-way89.com/static/media/hi-way-89-logo.13f6d083eca9c4c12c30.webp"
                className="w-16 h-20 mr-4 bg-white p-2"
                alt="HI-WAY 89 Logo"
              />
              <div>
                <h2 className="text-2xl font-black">HI-WAY 89</h2>
                <p className="text-gray-400 text-sm">ENTERTAINMENT</p>
              </div>
            </div>
            <p className="text-gray-400 text-lg leading-relaxed max-w-md mb-6">
              Where raw talent meets the open road of creativity. Driving the sound of tomorrow from Canada to the world.
            </p>
            <SocialIcons />
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-black text-lg mb-6 tracking-wider border-b border-gray-700 pb-2">
              NAVIGATE
            </h3>
            <ul className="space-y-3">
              {['Artists', 'Releases', 'Services', 'About'].map((link) => (
                <li key={link}>
                  <a 
                    href="#" 
                    className="text-gray-400 hover:text-white transition-all duration-200 text-sm tracking-wide flex items-center group"
                  >
                    <span className="w-1 h-1 bg-gray-600 mr-3 group-hover:bg-white transition-colors duration-200"></span>
                    {link.toUpperCase()}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-black text-lg mb-6 tracking-wider border-b border-gray-700 pb-2">
              CONTACT
            </h3>
            <div className="space-y-4 text-sm">
              <div className="group">
                <div className="text-white font-bold mb-1 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Inquiries / Submissions
                </div>
                <div className="text-gray-400 group-hover:text-white transition-colors duration-200">
                  admin@hi-way89.com
                </div>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-white font-black text-lg mb-6 tracking-wider border-b border-gray-700 pb-2">
              STAY CONNECTED
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Get exclusive updates on new releases and artists.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col space-y-3">
              <input
                type="email"
                placeholder="Your email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="bg-gray-900 border border-gray-700 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white transition-colors duration-200 text-sm disabled:opacity-50"
              />
              <button 
                type="submit" 
                className="bg-white text-black font-bold py-3 px-6 hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 text-sm tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? 'SUBSCRIBING...' : 'SUBSCRIBE'}
              </button>
              
              {status && (
                <p className={`text-sm mt-2 ${status.includes('✅') ? 'text-green-400' : 'text-red-400'}`}>
                  {status}
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="text-gray-400 text-sm mb-4 md:mb-0 flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Canada • WORLDWIDE
          </div>
          
          <div className="flex items-center space-x-6 text-sm text-gray-400">
            <span className="hidden md:block">© 2025 HI-WAY 89 ENTERTAINMENT</span>
          </div>
        </div>

        {/* Copyright for mobile */}
        <div className="md:hidden text-center text-gray-400 text-sm mt-4">
          © 2025 HI-WAY 89 ENTERTAINMENT
        </div>
      </div>
    </footer>
  );
};

export default Footer;